import { z } from "zod/v4";

// Live runs — the operator-facing trail of what AI-generated code did at
// runtime, shared by every codegen domain (shipping providers, Agentic Logic
// rules). One listen-window contract, one log-entry shape, one retention.
//
// A listen window is opened for at most LIVE_RUNS_MAX_LISTEN_WINDOW_MINUTES;
// while open, the serving broker captures the artifact's console output and
// records each run to a short-lived store that the UI polls. Runs are kept
// for LIVE_RUNS_RETENTION_MINUTES after they were recorded.

export const LIVE_RUNS_DEFAULT_LISTEN_WINDOW_MINUTES = 15;
export const LIVE_RUNS_MAX_LISTEN_WINDOW_MINUTES = 30;
export const LIVE_RUNS_RETENTION_MINUTES = 30;

// POST .../runs/listen — every domain mounts this body and response under
// its own path.

export const StartLiveRunsListenRequestBodySchema = z.object({
  windowMinutes: z
    .number()
    .int()
    .min(1)
    .max(LIVE_RUNS_MAX_LISTEN_WINDOW_MINUTES)
    .default(LIVE_RUNS_DEFAULT_LISTEN_WINDOW_MINUTES),
});
export type StartLiveRunsListenRequestBodyType = z.infer<
  typeof StartLiveRunsListenRequestBodySchema
>;

export const StartLiveRunsListenResponseBodySchema = z.object({
  /** Epoch ms the window closes. */
  listenWindowExpiresAt: z.number(),
});
export type StartLiveRunsListenResponseBodyType = z.infer<
  typeof StartLiveRunsListenResponseBodySchema
>;

/**
 * The window fact every run listing carries, so a polling view learns that a
 * window was closed elsewhere (another tab, the TTL) within one poll.
 */
export const LiveRunsListingWindowSchema = z.object({
  listenWindowExpiresAt: z.number().nullable(),
});

// Console output captured from the artifact (marker-logger protocol payload).

export const LiveRunsLogLevelList = [
  "log",
  "info",
  "warn",
  "error",
  "debug",
] as const;
export const LiveRunsLogLevelSchema = z.enum(LiveRunsLogLevelList);
export type LiveRunsLogLevel = (typeof LiveRunsLogLevelList)[number];

export const LiveRunsLogEntrySchema = z.object({
  level: LiveRunsLogLevelSchema,
  message: z.string(),
  details: z.unknown().optional(),
  timestamp: z.iso.datetime(),
});
export type LiveRunsLogEntry = z.infer<typeof LiveRunsLogEntrySchema>;
